import "./trait_selector.css";
import { Controller as BaseController } from "@hotwired/stimulus";
import Cap from "@cap.js/widget/";

const CAPTCHA_TIMEOUT_RESET = 1500;

function translateCount(scope, count) {
  if (count == 0) return scope["zero"];
  if (count == 1) return scope["one"];
  if (count == -1) return scope["all"];
  return scope["other"].replace("%{count}", count);
}

export class Controller extends BaseController {
  static targets = [
    "selectAll",
    "traitCheckbox",
    "countTaxons",
    "countTraits",
    "actionButton",
    "actionEmailButton",
    "actionEmailButtonText",
    "actionCsvText",
    // inputs
    "email",
    // hidden inputs
    "hiddenTaxons",
    "capToken",
  ];
  static values = {
    allTaxons: Boolean,
    taxonsCount: Number,
    traitsCount: Number,
    textDownloadCsv: String,
    textDownloadZip: String,
    textTaxons: Object,
    textTraits: Object,
    capApiEndpoint: String,
    listMode: Boolean,
    totalRowsCount: Number,
  };

  initialize() {
    this.cap = new Cap({
      apiEndpoint: this.capApiEndpointValue + "/",
    });
  }

  connect() {
    this.setupCapListeners();
    this.updateCounts();
    this.initialText = this.actionEmailButtonTextTarget.textContent;
  }

  disconnect() {
    this.destroyCapListeners();
  }

  // We ensure regular download CSV/ZIP does not trigger form validation
  // Thus why we need to enable it in the case of Send Email button
  async sendEmail(e) {
    this.emailTarget.required = true;

    if (!this.element.reportValidity()) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Reset cap token
    this.capTokenTarget.value = "";

    this.actionEmailButtonTextTarget.textContent = `Vérification...`;
    this.actionEmailButtonTarget.setAttribute("disabled", "disabled");
    this.actionEmailButtonTarget.setAttribute("data-state", "verifying");

    const { token } = await this.cap.solve();

    this.capTokenTarget.value = token;
    this.element.requestSubmit(this.actionEmailButtonTarget);
  }

  // Trick to ensure we don't trigger default button CSV
  // This will call sendEmail
  onEnterKey(e) {
    e.preventDefault();
    return this.actionEmailButtonTarget.click(e);
  }

  onSubmit() {
    // Reset email required state to allow CSV/ZIP after Send Email is clicked
    this.emailTarget.required = false;

    // Set as turbostream if email is filed
    this.element.setAttribute(
      "data-turbo",
      this.emailTarget.value.length > 1 ? "true" : "false"
    );

    // Empty if there is any selected
    this.hiddenTaxonsTarget.innerHTML = "";

    // Skip localStorage query if selected taxon is preset (taxons/:id)
    const taxonId = this.element["extract[taxon_ids][]"]?.value;
    if (taxonId) {
      return true;
    }

    const storedSelectAll = localStorage.getItem("selectAll");
    const storedSelectedIds = localStorage.getItem(
      this.listModeValue ? "listIds" : "selectedIds"
    );

    const selectAll = storedSelectAll ? JSON.parse(storedSelectAll) : false;
    const selectedIds = storedSelectedIds
      ? new Set(JSON.parse(storedSelectedIds))
      : new Set();

    // Otherwise create temporary inputs for the selected taxons from table
    let fragment = document.createDocumentFragment();

    selectedIds.forEach((id) => {
      const element = document.createElement("input");
      element.type = "hidden";
      element.name = "extract[taxon_ids][]";
      element.value = id;

      fragment.appendChild(element);
    });

    // Retrieve all query parameters and store them as inputs
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.forEach((value, key) => {
      if (!value) return;

      const element = document.createElement("input");
      element.type = "hidden";
      element.name = key;
      element.value = value;

      fragment.appendChild(element);
    });

    const element = document.createElement("input");
    element.type = "hidden";
    element.name = "extract[select_all]";
    element.value = selectAll;

    fragment.appendChild(element);

    this.hiddenTaxonsTarget.appendChild(fragment);

    return true;
  }

  toggleSelectAll() {
    const checked = this.selectAllTarget.checked;
    this.traitCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = checked;
    });
    this.updateCounts();
  }

  updateSelectAllState() {
    const allChecked = this.traitCheckboxTargets.every(
      (checkbox) => checkbox.checked
    );
    this.selectAllTarget.checked = allChecked;
    this.updateCounts();
  }

  updateCounts() {
    const selectedTraits = this.traitCheckboxTargets.filter(
      (cb) => cb.checked
    ).length;
    this.traitsCountValue =
      selectedTraits == this.traitCheckboxTargets.length ? -1 : selectedTraits;

    if (selectedTraits > 0) {
      this.actionButtonTargets.forEach((button) => {
        button.removeAttribute("disabled");
      });
      this.emailTarget.removeAttribute("disabled");
    } else {
      this.actionButtonTargets.forEach((button) => {
        button.setAttribute("disabled", "disabled");
      });
      this.emailTarget.setAttribute("disabled", "disabled");
    }
    this.actionCsvTextTarget.textContent =
      selectedTraits > 1
        ? this.textDownloadZipValue
        : this.textDownloadCsvValue;
  }

  updateTaxonsCount(taxons = 0, selectAll = false) {
    if (selectAll && taxons > 0) {
      this.taxonsCountValue = this.totalRowsCountValue - taxons;
    } else {
      this.taxonsCountValue = selectAll ? -1 : taxons;
    }
  }

  traitsCountValueChanged() {
    this.countTraitsTarget.textContent = translateCount(
      this.textTraitsValue,
      this.traitsCountValue
    );
  }

  taxonsCountValueChanged() {
    this.countTaxonsTarget.textContent = translateCount(
      this.textTaxonsValue,
      this.taxonsCountValue
    );
  }

  // Cap
  onCapSolve() {
    this.actionEmailButtonTextTarget.textContent = `Vérifié !`;
    this.actionEmailButtonTarget.setAttribute("data-state", "done");
    this.actionEmailButtonTarget.style.removeProperty("--progress");

    setTimeout(() => {
      this.onCapReset();
    }, CAPTCHA_TIMEOUT_RESET);
  }
  onCapProgress(e) {
    this.actionEmailButtonTarget.style.setProperty(
      "--progress",
      `${e.detail.progress}%`
    );
  }
  onCapError() {
    this.actionEmailButtonTextTarget.textContent = this.initialText;
    this.actionEmailButtonTarget.removeAttribute("disabled");
    this.actionEmailButtonTarget.setAttribute("data-state", "error");
  }
  onCapReset() {
    this.actionEmailButtonTextTarget.textContent = this.initialText;
    this.actionEmailButtonTarget.removeAttribute("disabled");
    this.actionEmailButtonTarget.removeAttribute("data-state");
    this.actionEmailButtonTarget.style.removeProperty("--progress");
  }

  setupCapListeners() {
    this.onCapProgress = this.onCapProgress.bind(this);
    this.onCapSolve = this.onCapSolve.bind(this);
    this.onCapError = this.onCapError.bind(this);

    this.cap.addEventListener("progress", this.onCapProgress);
    this.cap.addEventListener("solve", this.onCapSolve);
    this.cap.addEventListener("error", this.onCapError);
  }

  destroyCapListeners() {
    if (!this.cap) return;
    if (!this.cap.removeEventListener) return;

    this.cap.removeEventListener("progress", this.onCapProgress);
    this.cap.removeEventListener("solve", this.onCapSolve);
    this.cap.removeEventListener("error", this.onCapError);
  }
}
